<template>
  <div id="app">
    <HomePage v-if="page === 'home'" />
    <TaskBlockPage v-else-if="page === 'taskblock'" :sub="sub" />
    <AdminPage v-else-if="page === 'admin'" />
    <!-- 隐私政策 / 用户协议：独立页面，不依赖登录态 -->
    <LegalPage v-else-if="page === 'legal'" :type="sub || 'privacy'" />
  </div>
</template>

<script>
import HomePage from "@/components/home/HomePage.vue";
import TaskBlockPage from "@/components/taskblock/TaskBlockPage.vue";
import AdminPage from "@/components/admin/AdminPage.vue";
import LegalPage from "@/components/legal/LegalPage.vue";
import router from "@/router";
import { resetProfileState } from "@/components/taskblock/profileStorage";
import { logout as logoutApi } from "@/api/user";

// 「项目更新强制下线」检测用的版本戳存储键。
// 从旧的 'APP_BUILD_ID' 换名：旧键存的是「构建时间戳」（语义已废弃），
// 换新键能让存量用户首次加载时 stored 为空 → 不触发强制下线，避免改版瞬间把所有人踢下线。
const APP_VERSION_STORE_KEY = 'APP_VERSION'
// 旧键（构建时间戳）不再使用，主动清掉避免残留
try { localStorage.removeItem('APP_BUILD_ID') } catch (e) { /* ignore */ }

export default {
  name: 'App',
  components: {
    HomePage,
    TaskBlockPage,
    AdminPage,
    LegalPage
  },
  // 仍保留 provide 以兼容旧组件（MyNav 等）
  provide() {
    return {
      appState: router.state,
      setPage: router.setPage,
      router
    }
  },
  computed: {
    page() { return router.state.page },
    sub()  { return router.state.sub  }
  },
  created() {
    this.checkForceLogoutOnUpdate()
  },
  methods: {
    /**
     * 项目更新检测：
     * 版本戳取自 package.json 的 version（见 vue.config.js 的 APP_VERSION）。
     * 只有版本号变化（主动发版 / CI 注入新版本）时，才说明「项目发生了需要重新登录的更新」，
     * 此时强制清除登录态、重置共享个人状态、跳回首页，并弹出不可关闭的提示。
     * 常规前端发版不再改变版本戳，因此不会打断用户的 30 天免登录。
     */
    checkForceLogoutOnUpdate() {
      const appVersion = process.env.APP_VERSION
      const stored = localStorage.getItem(APP_VERSION_STORE_KEY)
      // 记录 / 同步当前版本戳（无版本号时落 'dev'，避免误判）
      localStorage.setItem(APP_VERSION_STORE_KEY, appVersion || 'dev')
      if (!appVersion) return
      // 首跑（本地无旧戳）或版本一致 -> 不处理
      if (!stored || stored === appVersion) return
      // 仅在曾登录（有 token）时才弹窗 + 强制下线；未登录用户静默更新即可
      if (!localStorage.getItem('token')) return
      this.forceLogout()
      this.$alert(
        '页面已更新，为确保你的账号安全，已自动退出登录。请重新登录后再查看账号内容。',
        '更新提示',
        {
          confirmButtonText: '我知道了',
          type: 'warning',
          showClose: false,
          closeOnClickModal: false,
          closeOnPressEscape: false
        }
      ).catch(() => {})
    },
    /**
     * 与 TaskNav.logout / ProfilePage 退出逻辑保持一致：清掉 token / user / taskblock-profile，
     * 并重置内存里的共享个人状态（昵称、头像、壁纸），避免残留上一个用户的信息。
     * 同步切回首页，防止任何账号内容继续渲染。
     */
    forceLogout() {
      // 先注销服务端 token（清 Redis 登录态），再清本地缓存；失败静默。
      // 必须在 removeItem('token') 之前调用，否则请求会带上空 token。
      logoutApi().catch(() => {})
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('taskblock-profile')
      try { resetProfileState() } catch (e) { /* ignore */ }
      // 同步切回首页：router.replace() 内部会同步刷新响应式状态并 replaceState，
      // 立即生效，不依赖 popstate 的异步时机（pushState/replaceState 不触发 popstate）。
      router.replace('/')
    }
  }
}
</script>

<style>
/* 英文字体 */
@font-face {
  font-family: 'DynaPuff';
  src: url('https://health-resource.suisuisu.cn/Font/DynaPuff/DynaPuff-VariableFont_wdth%2Cwght.ttf') format('truetype');
  font-weight: 400 700;
  font-style: normal;
  font-display: swap;
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}

/* 中文及符号字体 */
@font-face {
  font-family: 'OPPOSans';
  src: url('https://health-resource.suisuisu.cn/Font/ZCOOL/OPPO%20Sans%204.0.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
  unicode-range: U+4E00-9FFF, U+3400-4DBF, U+F900-FAFF, U+3000-303F, U+FF00-FFEF, U+2000-206F;
}


:root{
  --cream:#f7ede1;
  --cream-shape:#efd9c4;
  --navy:#2f4a5a;
  --accent-orange:#e8a33d;
  --dark-brown:#3a2b22;
  --pink:#e08a7d;
  --pink-dark:#d97363;
  --tan:#e3b98a;
  --tan-dark:#c98f57;
  --heart-pink:#e78a8a;

  /* ===== 轻量扁平主题令牌（表格页 / 顶栏 / 左侧工作区共用，唯一来源）=====
     子组件一律写 var(--lp-x, 兜底值)，所以脱离 App.vue 单独渲染也不会掉色。
     要调整表格页整屏配色，只改这一处。 */
  --lp-primary:#4aa46a;
  --lp-primary-dark:#2f7a4a;
  --lp-primary-soft:#e9f5ec;
  --lp-primary-soft2:#d5ebdb;
  --lp-border:#e3efe6;
  --lp-border-soft:#eef5f0;
  /* 板块/表格外框用的重一档描边：淡描边压在浅薄荷底上几乎看不见，
     这两个"块级"轮廓单独用深一点的绿，让板块边界清楚 */
  --lp-border-strong:#a5cdb3;
  --lp-surface:#ffffff;
  --lp-surface-2:#f7fbf8;
  --lp-bg:#eef7f0;
  --lp-text:#26332b;
  --lp-text-2:#5f7268;
  --lp-text-3:#94a59b;
  --lp-shadow:0 2px 10px rgba(52,102,68,.06);
  --lp-shadow-lg:0 8px 24px rgba(52,102,68,.08);
}

*{
  box-sizing:border-box;
  margin: 0;
  padding: 0;
}

html, body{
  height: 100%;
}

body{
  overflow:auto;
}

/*
 * 全局 root 字号断点适配：
 * 通过在不同屏幕宽度下调整 html 的 font-size，
 * 让全站 rem 数值等比缩放，实现响应式。
 *
 * 断点设计：
 *   <768px   : 14px (小屏/手机)        1rem = 14px
 *   768-1279 : 16px (平板)             1rem = 16px
 *   1280-1919: 18px (桌面)            1rem = 18px
 *   >=1920  : 20px (大屏/4K)         1rem = 20px
 *
 * 注意：rem 的根是 html 而非 body，必须设在 html 上。
 * 边框宽度、阴影偏移、布局结构宽度仍保留 px（见各组件）。
 */
html{
  font-size: 14px;
}

@media (min-width: 768px) {
  html { font-size: 16px; }
}

@media (min-width: 1280px) {
  html { font-size: 18px; }
}

@media (min-width: 1920px) {
  html { font-size: 20px; }
}

#app {
  font-family: 'DynaPuff', 'OPPOSans', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100%;
}

/* ===== Element UI 浮层主题（消息 / 确认框 / 日期时间面板）=====
 * 这些组件默认挂载在 body 下，组件内的 <style scoped> 够不到，
 * 只能在这里做全局覆盖。只改颜色 / 描边 / 圆角，尺寸与间距一律不动。
 * 用 --lp-* 令牌，跟表格页保持同一套配色。
 * 注意：首页（HomePage / 登录注册）完全不用 Element，所以这里的覆盖
 * 实际只作用于 TaskBlock 内部。
 */
.el-message-box {
  border: 3px solid var(--lp-border-strong, #a5cdb3);
  border-radius: 0.75rem;
}
.el-message-box__title {
  color: var(--lp-text, #26332b);
}
.el-message-box__content {
  color: var(--lp-text-2, #5f7268);
}
.el-message-box__headerbtn .el-message-box__close {
  color: var(--lp-text-3, #94a59b);
}
.el-message-box__headerbtn:hover .el-message-box__close,
.el-message-box__headerbtn:focus .el-message-box__close {
  color: var(--lp-text-2, #5f7268);
}
.el-message-box__status.el-icon-warning {
  color: var(--lp-primary, #4aa46a);
}
.el-message-box__status.el-icon-error {
  color: #d9534f;
}

/* 确认框 / 消息框里的按钮也走令牌（否则仍是 Element 默认灰 + 蓝）。
   注意：MessageBox 的确认按钮同时带 el-button--default 与 el-button--primary，
   所以 --default 必须写在前面，否则它带 :focus 的规则会把 primary 的底色盖掉。 */
.el-button--default {
  color: var(--lp-text-2, #5f7268);
  background: var(--lp-surface, #fff);
  border-color: var(--lp-border, #e3efe6);
}
.el-button--default:hover,
.el-button--default:focus {
  color: var(--lp-primary-dark, #2f7a4a);
  background: var(--lp-primary-soft, #e9f5ec);
  border-color: var(--lp-primary-soft2, #d5ebdb);
}
.el-button--primary,
.el-button--default.el-button--primary {
  color: #fff;
  background: var(--lp-primary, #4aa46a);
  border-color: var(--lp-primary, #4aa46a);
}
.el-button--primary:hover,
.el-button--primary:focus,
.el-button--default.el-button--primary:hover,
.el-button--default.el-button--primary:focus {
  color: #fff;
  background: var(--lp-primary-dark, #2f7a4a);
  border-color: var(--lp-primary-dark, #2f7a4a);
}
.el-button--primary.is-disabled,
.el-button--primary.is-disabled:hover,
.el-button--primary.is-disabled:focus {
  color: #fff;
  background: var(--lp-primary-soft2, #d5ebdb);
  border-color: var(--lp-primary-soft2, #d5ebdb);
}

.el-message {
  background: var(--lp-surface, #fff);
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 0.5rem;
  box-shadow: var(--lp-shadow-lg, 0 8px 24px rgba(52, 102, 68, 0.08));
}
.el-message .el-message__content {
  color: var(--lp-text, #26332b);
}
.el-message--success .el-message__content,
.el-message--success .el-message__icon {
  color: var(--lp-primary-dark, #2f7a4a);
}
.el-message--error .el-message__content,
.el-message--error .el-message__icon {
  color: #d9534f;
}
.el-message--warning .el-message__content,
.el-message--warning .el-message__icon {
  color: #b8860b;
}
.el-message--info .el-message__content,
.el-message--info .el-message__icon {
  color: var(--lp-text-2, #5f7268);
}

/* —— 右上角轻提示（el-notification：评论发送成功 / 失败用它） ——
 * 与 .el-message 一样挂在 body 下，scoped 够不到，只能在这里全局覆盖。
 * ⚠️ 同样写重复类名 `.el-notification.el-notification` 抬特异性（理由见下面 .el-dialog 的说明）：
 *   Element 自带 `.el-notification{border-radius:8px;border:1px solid #EBEEF5;...}`，
 *   单类名特异性相同，加载顺序落后时会把圆角/描边打回默认值。
 */
.el-notification.el-notification {
  background: var(--lp-surface, #fff);
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 0.75rem;
  box-shadow: var(--lp-shadow-lg, 0 8px 24px rgba(52, 102, 68, 0.08));
}
.el-notification.el-notification .el-notification__title {
  color: var(--lp-text, #26332b);
}
.el-notification.el-notification .el-notification__content {
  color: var(--lp-text-2, #5f7268);
}
.el-notification.el-notification .el-notification__closeBtn {
  color: var(--lp-text-3, #94a59b);
}
.el-notification.el-notification .el-notification__closeBtn:hover {
  color: var(--lp-text-2, #5f7268);
}
/* 图标色：成功走主题绿，失败/警告沿用 .el-message 的语义色 */
.el-notification.el-notification .el-icon-success {
  color: var(--lp-primary-dark, #2f7a4a);
}
.el-notification.el-notification .el-icon-error {
  color: #d9534f;
}
.el-notification.el-notification .el-icon-warning {
  color: #b8860b;
}
.el-notification.el-notification .el-icon-info {
  color: var(--lp-text-2, #5f7268);
}

/* —— 日期 / 时间选择面板（挂 body，scoped 够不到）—— */
.el-picker-panel {
  color: var(--lp-text-2, #5f7268);
  border-color: var(--lp-border, #e3efe6);
}
.el-picker-panel__icon-btn {
  color: var(--lp-text-2, #5f7268);
}
.el-picker-panel__icon-btn:hover {
  color: var(--lp-primary, #4aa46a);
}
.el-date-picker__header-label {
  color: var(--lp-text, #26332b);
}
.el-date-picker__header-label:hover {
  color: var(--lp-primary, #4aa46a);
}
.el-date-table th {
  color: var(--lp-text-3, #94a59b);
  border-bottom-color: var(--lp-border-soft, #eef5f0);
}
.el-date-table td.available:hover {
  color: var(--lp-primary-dark, #2f7a4a);
}
.el-date-table td.today span {
  color: var(--lp-primary, #4aa46a);
}
.el-date-table td.current:not(.disabled) span {
  background: var(--lp-primary, #4aa46a);
  color: #fff;
}
.el-date-table td.disabled div {
  color: var(--lp-text-3, #94a59b);
}
.el-picker-panel__footer {
  border-top-color: var(--lp-border-soft, #eef5f0);
}
.el-time-panel {
  border-color: var(--lp-border, #e3efe6);
}
.el-time-panel__content::before,
.el-time-panel__content::after {
  border-color: var(--lp-border, #e3efe6);
}
.el-time-panel__footer {
  border-top-color: var(--lp-border-soft, #eef5f0);
}
.el-time-panel__btn {
  color: var(--lp-text-2, #5f7268);
}
.el-time-panel__btn.confirm {
  color: var(--lp-primary, #4aa46a);
}
.el-time-spinner__item.active:not(.disabled) {
  color: var(--lp-text, #26332b);
}
.el-time-spinner__item:hover:not(.disabled):not(.active) {
  background: var(--lp-primary-soft, #e9f5ec);
}
.el-popper[x-placement^='bottom'] .popper__arrow,
.el-popper[x-placement^='bottom'] .popper__arrow::after {
  border-bottom-color: var(--lp-border, #e3efe6);
}
.el-popper[x-placement^='top'] .popper__arrow,
.el-popper[x-placement^='top'] .popper__arrow::after {
  border-top-color: var(--lp-border, #e3efe6);
}

/* —— 表单控件（仅换色，保持 Element 的 1px 描边宽度，不动尺寸）—— */
.el-input__inner,
.el-textarea__inner {
  color: var(--lp-text, #26332b);
  background: var(--lp-surface, #fff);
  border-color: var(--lp-border, #e3efe6);
}
.el-input__inner:focus,
.el-textarea__inner:focus {
  border-color: var(--lp-primary, #4aa46a);
}
.el-input__inner::placeholder,
.el-textarea__inner::placeholder {
  color: var(--lp-text-3, #94a59b);
}
.el-slider__runway {
  background-color: var(--lp-border-soft, #eef5f0);
}
.el-slider__stop {
  background-color: var(--lp-border, #e3efe6);
}
.el-slider__marks-text {
  color: var(--lp-text-3, #94a59b);
}
.el-radio-button__inner {
  color: var(--lp-text-2, #5f7268);
  background: var(--lp-surface, #fff);
  border-color: var(--lp-border, #e3efe6);
}

/* —— 对话框（el-dialog，好友申请 / 分享设置用它承载）——
 * 与 .share-dialog / .request-dialog 原来的手写外壳同一套观感：
 * 白底 + 3px 重描边 + 大圆角 + 柔阴影。
 * 这里的标题字号 / 关闭图标是**按用户要求放大**的（原 Element 默认 18px / 16px），
 * 不是"只换色"那套约束，改回去前请先确认需求。
 *
 * 2026-10-03 二次调整（用户明确要求）：
 *   描边色 --lp-border-strong(#a5cdb3) → --lp-primary(#4aa46a)，圆角 1rem → 1.5rem，
 *   标题 17 → 19px、正文 15px / 行高 1.6，内边距同步放大一档，整体更圆润、更松弛。
 *
 * 2026-10-04 三次调整（用户要求「去掉全部悬浮窗的绿色边框」）：
 *   绿色重描边 3px --lp-primary(#4aa46a) → **1px --lp-border(#e3efe6)** 极淡中性描边，
 *   白底 / 1.5rem 圆角 / 柔阴影不变。同一处理也套到手写悬浮窗外壳
 *   （.comment-dialog / .block-bubble / .create-ws-dialog / ThoughtsPage .modal），见各自组件。
 *
 * ⚠️ 选择器写成 `.el-dialog.el-dialog`（重复类名）是**故意的**，不要"化简"成单个 `.el-dialog`：
 *   Element 的 theme-chalk 里有一条 `.el-dialog{border-radius:2px;...}`，与单类名选择器特异性相同(0,1,0)。
 *   dev 模式下元样式由 JS 在运行时注入 <head> 末尾，晚于本文件的样式表 → 相同特异性下后者胜出，
 *   表现为「边框变绿了但圆角仍是 2px 直角」（边框能生效是因为 Element 没设 border，不存在冲突）。
 *   重复类名把特异性抬到 (0,2,0)，无论加载顺序如何都能压过 Element 默认值。
 *   → 实测验证：`.el-dialog.el-dialog` 下 computed border-radius = 24px（1.5rem）。
 *
 * ⚠️ 这是全局外壳，对 CategoryLegend 的 el-dialog 同样生效（保持全站弹窗观感一致）。
 */
.el-dialog.el-dialog {
  background: var(--lp-surface, #fff);
  border: 1px solid var(--lp-border, #e3efe6);
  border-radius: 1.5rem;
  box-shadow: var(--lp-shadow-lg, 0 8px 24px rgba(52, 102, 68, 0.08));
}

/* —— 弹窗垂直居中（全站 el-dialog 生效） ——
 * Element 的 el-dialog **没有垂直居中开关**：`top` 属性只是给 .el-dialog 设行内
 * `margin-top`（默认 15vh，好友页两个弹窗设的是 8vh），把框从顶部往下推 —— 无论怎么调
 * 都只是"离顶多远"，永远做不到真正的居中。
 * 所以这里把 fixed 全屏的 .el-dialog__wrapper 改造成 flex 居中容器：
 *   - align-items:center 负责垂直，justify-content:center 负责水平（取代原来的 margin:0 auto）；
 *   - 行内 `margin-top` 必须用 !important 才压得住（行内样式优先于任何选择器）。
 * ⚠️ .v-modal 遮罩是 position:fixed，不参与 flex 布局，即使塞进同一个父节点也不影响居中。
 * ⚠️ 若以后某个弹窗内容高过视口，flex 居中会把顶部溢出部分推到滚动区之外（够不到）；
 *    届时要给那个弹窗自己加 max-height + 内部滚动，不要改这里的 align-items。 */
.el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}
.el-dialog__wrapper > .el-dialog {
  margin: 0 !important;
}
/* 各子元素同样重复类名抬特异性，理由见上（Element 对这些选择器都有同特异性的默认值） */
.el-dialog.el-dialog .el-dialog__header {
  padding: 18px 22px 14px;
  border-bottom: 1px solid var(--lp-border-soft, #eef5f0);
}
.el-dialog.el-dialog .el-dialog__title {
  font-size: 19px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--lp-text, #26332b);
}
.el-dialog.el-dialog .el-dialog__headerbtn {
  top: 16px;
  right: 18px;
  width: 32px;
  height: 32px;
}
.el-dialog.el-dialog .el-dialog__headerbtn .el-dialog__close {
  font-size: 20px;
  color: var(--lp-text-3, #94a59b);
}
.el-dialog.el-dialog .el-dialog__headerbtn:hover .el-dialog__close,
.el-dialog.el-dialog .el-dialog__headerbtn:focus .el-dialog__close {
  color: var(--lp-primary-dark, #2f7a4a);
}
.el-dialog.el-dialog .el-dialog__body {
  padding: 18px 22px;
  font-size: 15px;
  line-height: 1.6;
  color: var(--lp-text-2, #5f7268);
}
.el-dialog.el-dialog .el-dialog__footer {
  padding: 0 22px 20px;
  border-top: none;
}
/* 遮罩色统一成 app 里其它浮层的 rgba(38,51,43,.35)（见 .workbuddy-ai/style-spec.md）。
 * ⚠️ 必须显式写 opacity:1：Element 自带的 .v-modal 有 opacity:.5，
 * 会和 background 的 alpha 相乘，把 0.35 压成实际只有 0.175 —— 实测几乎看不出压暗。 */
.v-modal {
  background: rgba(38, 51, 43, 0.35);
  opacity: 1;
}

/* —— 「添加分类」弹窗（CategoryLegend.vue）的收窄微调 ——
 * 上面的 .el-dialog 全局外壳已经给了白底 / 重描边 / 大圆角 / 柔阴影，
 * 这里只补两件它没覆盖的事：
 *   1) 小屏不溢出（宽度由组件自己给，两列等宽布局下是 720px）；
 *   2) 描边/圆角收一档 —— 1.5rem + 3px 对这个小表单略显笨重。
 * 必须放在这里而不是组件内部：该弹窗开了 append-to-body，DOM 挂在 body 上，
 * 组件 scoped 样式与 `>>>` 深度选择器都命中不了它。 */
.cat-dialog.el-dialog {
  border-radius: 1rem;
  max-width: 92vw;
}
.cat-dialog .el-radio-button__inner {
  border-radius: 0;
}
.cat-dialog .el-radio-button:first-child .el-radio-button__inner {
  border-radius: 0.5rem 0 0 0.5rem;
}
.cat-dialog .el-radio-button:last-child .el-radio-button__inner {
  border-radius: 0 0.5rem 0.5rem 0;
}
.cat-dialog .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background: var(--lp-primary, #4aa46a);
  border-color: var(--lp-primary, #4aa46a);
  box-shadow: -1px 0 0 0 var(--lp-primary, #4aa46a);
}
.cat-dialog .el-button--primary {
  background: var(--lp-primary, #4aa46a);
  border-color: var(--lp-primary, #4aa46a);
}
.cat-dialog .el-button--primary:hover,
.cat-dialog .el-button--primary:focus {
  background: var(--lp-primary-dark, #2f7a4a);
  border-color: var(--lp-primary-dark, #2f7a4a);
}

</style>
